import { useState } from 'react'
import { createPortal } from 'react-dom'
import { Heart } from 'lucide-react'
import { PAYPAL_ME_URL, DONATION_PRESET_AMOUNTS } from '../data/constants.jsx'

const DonationModal = ({ isOpen, onClose }) => {
  const [selectedAmount, setSelectedAmount] = useState(10)
  const [customAmount, setCustomAmount] = useState('')

  const isCustomMode = selectedAmount === null

  if (!isOpen) return null

  const canDonate = () => {
    if (!isCustomMode) return true
    const parsed = parseFloat(customAmount)
    return !isNaN(parsed) && parsed > 0
  }

  const handleDonate = () => {
    const amount = isCustomMode ? parseFloat(customAmount) : selectedAmount
    const url = amount && amount > 0 ? `${PAYPAL_ME_URL}/${amount}` : PAYPAL_ME_URL
    window.open(url, '_blank', 'noopener,noreferrer')
    onClose()
  }

  const handlePreset = (amount) => {
    setSelectedAmount(amount)
    setCustomAmount('')
  }

  const handleCustomChange = (e) => {
    setSelectedAmount(null)
    setCustomAmount(e.target.value)
  }

  return createPortal(
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-[200] p-4"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="bg-card border border-border rounded-xl max-w-sm w-full p-6 shadow-strong">
        {/* Header */}
        <div className="flex justify-between items-start mb-1">
          <div className="flex items-center gap-2">
            <Heart className="w-5 h-5 fill-rose-500 text-rose-500 shrink-0" />
            <h2 className="text-lg font-bold text-foreground">Support IqraWave</h2>
          </div>
          <button
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground text-2xl leading-none ml-2 -mt-1"
            aria-label="Close"
          >
            ×
          </button>
        </div>

        <p className="text-sm text-muted-foreground text-center mb-5 mt-2">
          Help us continue providing free Fajr wake-up services for everyone.
        </p>

        {/* Preset amounts */}
        <div className="grid grid-cols-4 gap-2 mb-4">
          {DONATION_PRESET_AMOUNTS.map((preset) => (
            <button
              key={preset.amount}
              onClick={() => handlePreset(preset.amount)}
              className={`rounded-lg border py-2 text-sm font-medium transition-colors ${
                selectedAmount === preset.amount
                  ? 'border-primary bg-primary text-primary-foreground'
                  : 'border-border text-foreground hover:border-primary hover:text-primary'
              }`}
            >
              {preset.label}
            </button>
          ))}
        </div>

        {/* Custom amount */}
        <div className="relative mb-4">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground pointer-events-none">
            $
          </span>
          <input
            type="number"
            min="1"
            placeholder="Custom amount"
            value={customAmount}
            onChange={handleCustomChange}
            className="w-full pl-7 pr-3 py-2 border border-border rounded-lg bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary placeholder:text-muted-foreground"
          />
        </div>

        {/* Donate button */}
        <button
          onClick={handleDonate}
          disabled={!canDonate()}
          className="w-full bg-gradient-primary text-primary-foreground py-2.5 rounded-lg font-medium text-sm flex items-center justify-center gap-2 hover:opacity-90 transition-opacity disabled:opacity-40 disabled:cursor-not-allowed shadow-soft"
        >
          <Heart className="w-4 h-4 fill-current" />
          Donate with PayPal
        </button>

        <p className="text-center text-xs text-muted-foreground mt-3">
          You&apos;ll be redirected to PayPal to complete your donation securely.
        </p>
      </div>
    </div>,
    document.body
  )
}

export default DonationModal
